import type { UnknownAction } from '@reduxjs/toolkit';
import { isNil } from 'lodash-es';

import { log } from '../../services/logging';
import migrate from '../../services/migrate';
import type { Scene } from '../../types/scene';
import { getUserId, isPerspectiveSharingAvailable } from '../connections/connectionsDuck';
import type { Persistor } from '../persistence/types';
import { SET_CURRENT_PERSPECTIVE, removePerspective } from '../perspectives/perspectives';
import { getPerspective } from '../perspectives/perspectives.selector';
import { clearState, unsetCurrentPerspective } from '../rootReducer';
import { getEnableCytoscape } from '../settings/settings';
import { isSharedStorageAvailable } from '../shared-storage/shared-storage.api';
import localStorage from '../storage/localStorage';
import type { store as RootStore } from '../store';
import { selectStyleById } from '../styles/styles.selectors';
import type { RootState } from '../types';
import { trasformations } from './migrations';
import { addScene } from './scene';
import { SceneTransformer } from './scene-transformer';
import { getCurrentScene, getStartWithProvidedGraph, isStartWithNewSceneActive } from './scene.selectors';
import { applySelectedSceneThunk } from './thunks/apply-selected-scene';
import { buildScene } from './thunks/store-scene';

const PREFIX = 'neo4j.bloomScene';

export class LocalStorageScenePersistor implements Persistor {
  private readonly sceneTransformer: SceneTransformer;

  constructor() {
    this.sceneTransformer = new SceneTransformer();
  }

  getKey(state: RootState): string | null {
    const userId = getUserId(state);
    const perspective = getPerspective(state);

    if (!perspective || !userId) {
      log.info(`Missing data to save/load scene - userId: ${userId}, perspectiveId:${perspective?.id}`);
      return null;
    }

    const { id, dbmsId, dbId } = perspective;
    return `${PREFIX}.${dbmsId}.${dbId}.${userId}.${id}`;
  }

  isEligible(state: RootState): boolean {
    const isPluginPresent = Boolean(state?.connections?.procedures && isPerspectiveSharingAvailable(state));
    if (isPluginPresent || isSharedStorageAvailable()) {
      return false;
    }

    return true;
  }

  shouldLoadState({ action, state }: { action: UnknownAction | null; state: RootState }): boolean {
    if (!this.isEligible(state)) return false;
    // Workaround to prevent local scene from overwriting Explore graph results
    // TODO remove when we consolidate scene loading logic
    if (getStartWithProvidedGraph(state)) return false;

    return action?.type === SET_CURRENT_PERSPECTIVE;
  }

  async loadFromStorage({
    store,
    payload,
    action,
  }: {
    store: typeof RootStore;
    payload: object;
    action: UnknownAction;
  }): Promise<object | void> {
    const { dispatch, getState } = store;

    const state = getState();

    const isFirstLogin = isNil(getPerspective(state));

    if (isNil(action)) {
      if (!isFirstLogin) {
        dispatch(unsetCurrentPerspective());
      }
    }

    const scene = await this.loadScenes({ store });
    if (!scene) {
      log.error('Failed to load existing scene or build a new one');
      return payload;
    }

    dispatch(clearState());
    dispatch(addScene(scene));
    await dispatch(applySelectedSceneThunk({ sceneId: scene.id, isGraphProvided: true }));

    return payload;
  }

  shouldSaveState({ state, action }: { state: RootState; action: UnknownAction }): boolean {
    if (!this.isEligible(state)) return false;

    const hasPerspective = getPerspective(state) !== undefined;
    const hasScene = 'scene' in state && 'scenes' in state.scene && getCurrentScene(state) !== undefined;

    return (hasPerspective && hasScene) || removePerspective.match(action);
  }

  buildEmptyScene(state: RootState): Scene | undefined {
    const userId = getUserId(state);
    const newScene = buildScene(userId);
    if (!newScene) {
      log.error('Failed to build a new scene');
      return;
    }

    return {
      ...newScene,
      name: `Local Scene`,
    };
  }

  async loadScenes({ store }: { store: typeof RootStore }): Promise<Scene | undefined> {
    const state = store.getState();
    const key = this.getKey(state);
    if (!key) return;
    const isStartWithNewScene = isStartWithNewSceneActive(state);

    if (isStartWithNewScene) {
      return this.buildEmptyScene(state);
    }

    const loaded: Scene | null = await localStorage.getItem(key);
    if (loaded) {
      const perspective = getPerspective(state);
      if (!perspective) return;
      const perspectiveStyle = selectStyleById(state)(perspective.id);
      const enableCytoscape = getEnableCytoscape(state);

      const migratedScene: Scene = migrate(loaded, trasformations(perspectiveStyle, enableCytoscape), loaded.version);
      return migratedScene;
    }

    return this.buildEmptyScene(state);
  }

  async removeScenesByPerspectiveId(perspectiveId: string): Promise<void> {
    try {
      const allKeys = await localStorage.keys();
      const pattern = new RegExp(`^${PREFIX}\\..*\\..*\\..*\\.${perspectiveId}$`);
      const orphanedSceneKeys = allKeys.filter((key) => pattern.test(key));

      for (const key of orphanedSceneKeys) {
        await localStorage.removeItem(key);
      }
    } catch (e: unknown) {
      log.warn(`Failed to remove scenes on perspective deletion: ${e}`);
    }
  }

  async saveToStorage({ state, action }: { state: RootState; action: UnknownAction }): Promise<void> {
    if (removePerspective.match(action)) {
      await this.removeScenesByPerspectiveId(action.payload.perspectiveId);
      return;
    }

    const scene = getCurrentScene(state);
    if (!scene) {
      log.info(`Missing scene data to save scene: ${scene}`);
      return;
    }

    const fullScene = this.sceneTransformer.getFullSceneFromCurrentGraph(state, scene);
    const key = this.getKey(state);
    if (!key) {
      log.info('Missing key to save scene data');
      return;
    }

    try {
      await localStorage.setItem(key, fullScene);
    } catch (e) {
      log.error(`Unable to save scene:`, e);
    }
  }
}
